import { useEffect, useCallback, useState } from 'react'
import { useAuthenticatedFetch, type Generation } from '../lib/api'
import { getModelById } from '../lib/models'
import { downloadGeneration } from '../lib/share'
import VideoPlayer from './VideoPlayer'
import ShareButton from './ShareButton'

interface FullscreenModalProps {
  generation: Generation
  onClose: () => void
  onDelete: (id: string) => void
  onReuse?: (generation: Generation) => void
  onShare?: (generation: Generation) => void
}

export default function FullscreenModal({
  generation,
  onClose,
  onDelete,
  onReuse,
}: FullscreenModalProps) {
  const model = getModelById(generation.model)
  const authenticatedFetch = useAuthenticatedFetch()
  const [describePrompts, setDescribePrompts] = useState<string[] | null>(null)
  const [describeLoading, setDescribeLoading] = useState(false)
  const [describeError, setDescribeError] = useState<string | null>(null)

  const handleDescribe = useCallback(async () => {
    if (!generation.r2_url || generation.type !== 'image') return
    setDescribeLoading(true)
    setDescribeError(null)
    setDescribePrompts(null)
    try {
      const res = await authenticatedFetch('/api/describe', {
        method: 'POST',
        body: JSON.stringify({ image_url: generation.r2_url }),
      })
      const { data } = (await res.json()) as { data: { prompts: string[] } }
      setDescribePrompts(data.prompts)
    } catch (err) {
      setDescribeError(err instanceof Error ? err.message : 'Describe failed')
    } finally {
      setDescribeLoading(false)
    }
  }, [authenticatedFetch, generation.r2_url, generation.type])

  const handlePickDescribed = useCallback(
    (prompt: string) => {
      onReuse?.({ ...generation, prompt })
      onClose()
    },
    [generation, onReuse, onClose],
  )

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    },
    [onClose],
  )

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown)
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.body.style.overflow = ''
    }
  }, [handleKeyDown])

  const handleBackdropClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) onClose()
    },
    [onClose],
  )

  const handleDownload = useCallback(() => {
    void downloadGeneration(generation).catch((err) => {
      console.error('Download failed:', err)
    })
  }, [generation])

  const handleReuse = useCallback(() => {
    onReuse?.(generation)
    onClose()
  }, [generation, onReuse, onClose])

  const handleDelete = useCallback(() => {
    onDelete(generation.id)
    onClose()
  }, [generation.id, onDelete, onClose])

  const formattedDate = new Date(generation.created_at).toLocaleString()

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm"
      onClick={handleBackdropClick}
    >
      <button
        onClick={onClose}
        className="absolute top-4 right-4 z-10 p-2 text-text-secondary hover:text-text-primary transition-colors cursor-pointer"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>

      <div className="flex max-w-6xl w-full max-h-[90vh] mx-4 bg-bg-card rounded-2xl overflow-hidden border border-border">
        <div className="flex-1 flex items-center justify-center bg-black min-h-0">
          {generation.type === 'video' ? (
            <VideoPlayer
              src={generation.r2_url ?? ''}
              poster={generation.thumbnail_r2_key}
              controls
              className="max-h-[90vh] object-contain"
            />
          ) : (
            <img
              src={generation.r2_url ?? ''}
              alt={generation.prompt}
              className="max-h-[90vh] max-w-full object-contain"
            />
          )}
        </div>

        <div className="w-80 shrink-0 flex flex-col border-l border-border">
          <div className="flex-1 overflow-y-auto p-5 space-y-5">
            <div className="space-y-1.5">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-text-muted">
                Prompt
              </h3>
              <p className="text-sm text-text-primary leading-relaxed">
                {generation.prompt}
              </p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <DetailItem label="Model" value={model?.name ?? generation.model} />
              <DetailItem label="Type" value={generation.type} />
              {generation.width && generation.height && (
                <DetailItem
                  label="Dimensions"
                  value={`${generation.width}x${generation.height}`}
                />
              )}
              {generation.steps && (
                <DetailItem label="Steps" value={String(generation.steps)} />
              )}
              {generation.seed !== undefined && generation.seed !== null && (
                <DetailItem label="Seed" value={String(generation.seed)} />
              )}
              <DetailItem label="Created" value={formattedDate} />
            </div>

            {generation.nsfw && (
              <span className="inline-block px-2 py-1 text-xs font-semibold bg-error/20 text-error rounded">
                NSFW
              </span>
            )}

            {generation.type === 'image' && generation.r2_url && (
              <div className="space-y-2">
                <button
                  onClick={handleDescribe}
                  disabled={describeLoading}
                  className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg border border-neon/40 bg-neon/5 hover:bg-neon/15 text-neon text-xs font-semibold transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {describeLoading ? (
                    <>
                      <span className="w-3 h-3 rounded-full border-2 border-neon border-t-transparent animate-spin" />
                      Describing…
                    </>
                  ) : (
                    <>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M12 2L2 7l10 5 10-5-10-5z" />
                        <path d="M2 17l10 5 10-5" />
                        <path d="M2 12l10 5 10-5" />
                      </svg>
                      {describePrompts ? 'Describe again' : 'Describe this image'}
                    </>
                  )}
                </button>

                {describeError && (
                  <p className="text-[11px] text-error">{describeError}</p>
                )}

                {describePrompts && describePrompts.length > 0 && (
                  <div className="space-y-2">
                    <h4 className="text-[10px] font-semibold uppercase tracking-wider text-text-muted">
                      4 prompts to recreate
                    </h4>
                    {describePrompts.map((p, i) => (
                      <button
                        key={i}
                        onClick={() => handlePickDescribed(p)}
                        className="w-full text-left p-2.5 rounded-lg border border-border hover:border-neon hover:bg-neon/5 bg-bg-secondary transition-colors cursor-pointer group"
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-[10px] font-bold text-neon/70 group-hover:text-neon">
                            Variant {i + 1}
                          </span>
                          <span className="text-[10px] text-text-muted group-hover:text-text-secondary">
                            Use →
                          </span>
                        </div>
                        <p className="text-[11px] text-text-secondary leading-relaxed">
                          {p}
                        </p>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="flex items-center gap-2 p-4 border-t border-border">
            {onReuse && (
              <button
                onClick={handleReuse}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-neon hover:bg-neon/10 transition-colors rounded-lg cursor-pointer font-semibold"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="1 4 1 10 7 10" />
                  <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
                </svg>
                Use prompt
              </button>
            )}
            <button
              onClick={handleDownload}
              disabled={!generation.r2_url}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-text-secondary hover:text-neon transition-colors rounded-lg hover:bg-bg-elevated cursor-pointer disabled:opacity-50"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
              Download
            </button>
            <ShareButton
              title="FRANKENVIBES LAB"
              text={generation.prompt}
              url={generation.r2_url ?? window.location.href}
            />
            <button
              onClick={handleDelete}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-text-secondary hover:text-error transition-colors rounded-lg hover:bg-bg-elevated cursor-pointer ml-auto"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="3 6 5 6 21 6" />
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
              </svg>
              Delete
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

function DetailItem({ label, value }: { label: string; value: string }) {
  return (
    <div className="space-y-0.5">
      <dt className="text-xs text-text-muted">{label}</dt>
      <dd className="text-sm text-text-secondary">{value}</dd>
    </div>
  )
}
